import { z } from 'zod';
import { inferenceHttp } from '@/lib/api/http-client';
import { ArtifactsService } from '@inference.engine/api-client/web';
import type { IdeationWidget } from '@inference.engine/widgets/web';
import { getSavedArtifactSnapshot } from '@/entities/artifact';
import { downloadArtifactPdfV2 } from '@/shared/api/pdf-download.api';
import { logger } from '@/lib/utils/logger';
import type { IdeationWidgetDataUnion } from '../model/ideation-widget.types';

// READ schema for the print page — deliberately looser than the GENERATION
// schema (`IdeationWidgetSchema`). It validates only the shape the render
// indexes into and tolerates historical/partial artifacts, since the generation
// schema's cardinality/range rules (`ideas`/`sparks` .min(1), scores exactly
// `.length(5)`, `score` 10-100) reject real saved campaigns that the fully
// defensive render handles fine. `normalizeWidgetData` reconciles V1/V2 later,
// so one permissive shape covers both; passthrough keeps every other field.
const IdeationCampaignReadSchema = z
  .object({
    title: z.string(),
    ideas: z.array(z.looseObject({})),
  })
  .loose();

const IDEATION_CAMPAIGN_SAVE_KIND = 'ideation-campaign';
const IDEATION_IDEA_SAVE_KIND = 'ideation-idea';

/** Saves the whole ideation campaign and returns the saved artifact's id. */
export const saveIdeationCampaignArtifact = async (
  workspaceId: string,
  sourceArtifactId: string,
): Promise<string> => {
  const response = await ArtifactsService.saveWidgetArtifact({
    client: inferenceHttp,
    path: { workspaceId, kind: IDEATION_CAMPAIGN_SAVE_KIND },
    body: { sourceArtifactId },
  });

  if (!response.data) {
    throw new Error('Failed to save ideation campaign artifact');
  }

  return response.data.id;
};

/** Saves the idea at `ideaIndex` and returns the saved artifact's id. */
export const saveIdeationIdeaArtifact = async (
  workspaceId: string,
  sourceArtifactId: string,
  ideaIndex: number,
): Promise<string> => {
  const response = await ArtifactsService.saveWidgetArtifact({
    client: inferenceHttp,
    path: { workspaceId, kind: IDEATION_IDEA_SAVE_KIND },
    body: { sourceArtifactId, options: { itemIndex: ideaIndex } },
  });

  if (!response.data) {
    throw new Error('Failed to save ideation idea artifact');
  }

  return response.data.id;
};

export const getSavedIdeationSnapshot = (
  workspaceId: string,
  artifactId: string,
): Promise<IdeationWidget> => getSavedArtifactSnapshot<IdeationWidget>(workspaceId, artifactId);

/** Full campaign payload (V1 or V2) for the `/download/ideation/:id` print page. */
export const getSingleCampaign = async (
  artifactId: string,
  { workspaceId, itemIndex }: { workspaceId: string; itemIndex?: number },
): Promise<IdeationWidgetDataUnion> => {
  const snapshot = await getSavedArtifactSnapshot<unknown>(workspaceId, artifactId);
  // Shape-check at the fetch boundary so a genuinely malformed snapshot fails
  // here instead of crashing deep in the print render. react-query surfaces a
  // throw only as `isError`, so log the issues to keep the failure reproducible.
  const parsed = IdeationCampaignReadSchema.safeParse(snapshot);
  if (!parsed.success) {
    logger.error('Malformed ideation campaign snapshot', {
      artifactId,
      issues: parsed.error.issues,
    });
    throw new Error(`Malformed ideation campaign snapshot ${artifactId}`);
  }
  const data = snapshot as IdeationWidgetDataUnion;

  // A missing itemIndex means the overview; a provided one must be a valid number
  if (
    itemIndex != null &&
    (!Number.isInteger(itemIndex) || itemIndex < 0 || itemIndex >= data.ideas.length)
  ) {
    throw new Error(`No idea at index ${itemIndex} in ideation campaign ${artifactId}`);
  }

  return data;
};

/** Server-rendered campaign-overview PDF via the generic v2 artifact endpoint. */
export const downloadCampaignPdf = (
  artifactId: string,
  { workspaceId }: { workspaceId: string },
): Promise<Blob> =>
  downloadArtifactPdfV2(artifactId, {
    // eslint-disable-next-line i18next/no-literal-string -- interpolated into the diagnostic `pdf-download.api.ts` throws; the caller toasts its own translated message
    label: 'Ideation campaign',
    // eslint-disable-next-line i18next/no-literal-string -- server-side document-type token
    documentType: 'ideation',
    workspaceId,
  });

/** Server-rendered PDF of one idea's detail; `itemIndex` picks the idea. */
export const downloadIdeaPdf = (
  artifactId: string,
  { workspaceId, itemIndex }: { workspaceId: string; itemIndex: number },
): Promise<Blob> =>
  downloadArtifactPdfV2(artifactId, {
    // eslint-disable-next-line i18next/no-literal-string -- interpolated into the diagnostic `pdf-download.api.ts` throws; the caller toasts its own translated message
    label: 'Ideation idea',
    // eslint-disable-next-line i18next/no-literal-string -- server-side document-type token
    documentType: 'ideation',
    workspaceId,
    itemIndex,
  });
